import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { adminChatApi } from '../api/adminChatApi';
import useAuth from '../hooks/useAuth';
import { hasAccess } from '../lib/hasAccess';

const defaultCounts = { consultations: 0, coaching: 0, support: 0, iop: 0 };
const EMPTY_ROLES = [];

const ChatContext = createContext({
  waitingResponseCounts: defaultCounts,
  totalWaiting: 0,
  loading: false,
  refreshWaitingCounts: () => {},
  applyWaitingCounts: () => {},
});

export const useChat = () => useContext(ChatContext);

/**
 * Polls GET /admin/chats/waiting-counts for `waiting_response_counts`
 * (consultations / coaching / support).
 * `totalWaiting` sums only buckets allowed by ACCESS (`coachingChats`, `supportChats`).
 *
 * Callers that already hold a chat-list response should feed its
 * `waiting_response_counts` straight into `applyWaitingCounts` rather than
 * calling `refreshWaitingCounts` — the list endpoint returns the same tallies,
 * so refetching them is a duplicate round-trip.
 */
export const ChatProvider = ({ children }) => {
  const { user } = useAuth();
  const userRoles = Array.isArray(user?.roles) ? user.roles : EMPTY_ROLES;
  const includeCoachingInTotals = hasAccess('coachingChats', userRoles);
  const includeSupportInTotals = hasAccess('supportChats', userRoles);
  const includeIopInTotals = hasAccess('iopChats', userRoles);

  const [waitingResponseCounts, setWaitingResponseCounts] = useState(defaultCounts);
  const [loading, setLoading] = useState(false);

  const applyWaitingCounts = useCallback((counts) => {
    const next = counts || defaultCounts;
    setWaitingResponseCounts({
      consultations: Number(next.consultations) || 0,
      coaching: includeCoachingInTotals ? (Number(next.coaching) || 0) : 0,
      support: includeSupportInTotals ? (Number(next.support) || 0) : 0,
      iop: includeIopInTotals ? (Number(next.iop) || 0) : 0,
    });
  }, [includeCoachingInTotals, includeSupportInTotals, includeIopInTotals]);

  const fetchWaitingCounts = useCallback(async () => {
    const token = window.localStorage.getItem('accessToken');
    if (!token) return;

    setLoading(true);
    try {
      const data = await adminChatApi.getWaitingCounts();
      applyWaitingCounts(data.waiting_response_counts);
    } catch (err) {
      console.error('Failed to fetch waiting response counts:', err);
      setWaitingResponseCounts(defaultCounts);
    } finally {
      setLoading(false);
    }
  }, [applyWaitingCounts]);

  useEffect(() => {
    fetchWaitingCounts();
    const interval = setInterval(fetchWaitingCounts, 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchWaitingCounts]);

  const totalWaiting = useMemo(() => {
    const w = waitingResponseCounts;
    let n = w.consultations;
    if (includeCoachingInTotals) n += w.coaching;
    if (includeSupportInTotals) n += w.support;
    if (includeIopInTotals) n += w.iop;
    return n;
  }, [waitingResponseCounts, includeCoachingInTotals, includeSupportInTotals]);

  const value = {
    waitingResponseCounts,
    totalWaiting,
    loading,
    refreshWaitingCounts: fetchWaitingCounts,
    applyWaitingCounts,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export default ChatContext;
